import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddressAutocomplete = ({ value, onChange, placeholder, className, dataTestId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const skipNextFetch = useRef(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/places/autocomplete`, { params: { input: value } });
        const preds = res.data.predictions || [];
        setSuggestions(preds);
        setOpen(preds.length > 0);
        setHighlight(-1);
      } catch {
        setSuggestions([]);
        setOpen(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const select = (desc) => {
    skipNextFetch.current = true;
    onChange(desc);
    setSuggestions([]);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); select(suggestions[highlight].description); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        data-testid={dataTestId}
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-[#161616] border border-[#D4AF37]/25 rounded-sm shadow-2xl overflow-hidden" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={s.place_id || i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); select(s.description); }}
              className={`w-full text-left px-4 py-2.5 font-manrope text-sm transition-colors ${i === highlight ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
              data-testid={`address-suggestion-${i}`}
            >
              {s.description}
            </button>
          ))}
          <div className="px-4 py-1.5 text-right text-white/20 font-manrope text-[9px] uppercase tracking-widest border-t border-white/5">Powered by Google</div>
        </div>
      )}
    </div>
  );
};
